<template>
  <div
    class="ContentHomeHeader main-header"
    :class="{ 'is--mounted': isMounted, 'is--offerings': isFarmExist }"
  >
    <div
      class="main-header__wrapper"
      :class="{ 'is--no-offerings': !isFarmExist, 'is--one-farm': isOneFarm }"
    >
      <div
        class="main-header__left main-header-left"
        :class="{ 'is--no-offerings': !isFarmExist, 'is--offerings': isFarmExist, 'is--one-farm': isOneFarm }"
      >
        <div
          class="main-header-left__wrapper"
          :class="{ 'is--offerings': isFarmExist }"
        >
          <div class="main-header-left__text">
            <h1 class="hero main-header-left__title">
              <div class="main-header-left__title-wrapper">
                <div
                  v-if="!isFarmExist"
                  class="main-header-left__title-wrapper-absolute"
                >
                  <transition
                    mode="out-in"
                    name="slide-up"
                  >
                    <span
                      :key="activeTitle"
                      class="main-header-left__title-absolute"
                    >{{ activeTitle }}
                    </span>
                  </transition>
                </div>
                <span
                  v-if="!isFarmExist"
                  class="main-header-left__title-investing"
                  :style="{ marginLeft: '' }"
                >Investing.</span>
              </div>
              <span
                v-if="isFarmExist"
                class="main-header-left__title-land"
              >Farmland Investing.</span>
              <div>Simplified.</div>
            </h1>
            <div class="is-h3__title subheading main-header-left__subheading">
              Diversify with an asset class that has the potential to create wealth opportunities over time.
            </div>
          </div>
          <div
            class="main-header__get-started"
            :class="{ 'is--not-logged-in': !isLoggedIn }"
          >
            <FormGetStarted
              class=""
              :is-logged-in="isLoggedIn"
            />
          </div>
        </div>
        <AsSeenOnLogos
          class="main-header__as-seen-on-logos"
          :class="{ 'is--offerings': isFarmExist }"
        />
      </div>
      <div
        class="main-header__right main-header-right"
        :class="{ 'is--no-offerings': !isFarmExist }"
      >
        <ContentHomeSlider
          v-if="!isFarmExist"
          class="main-header-right__pattern-slider"
          @slider-image-change="sliderCurrentImage = $event"
          @current-slide-change="onCurrentSlideChange"
        />
        <FarmSlider
          v-if="isFarmExist"
          class="main-header-right__slider"
          @slider-image-change="sliderCurrentImage = $event"
        />
        <svg
          class="main-header__farm-background"
          width="935"
          height="704"
          viewBox="0 0 935 704"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <clipPath id="circleView">
              <path
                d="M0 0H935V704L351 267L0 0Z"
                fill="white"
              />
            </clipPath>
          </defs>
          <image
            clas="main-header__farm-background-image"
            :href="sliderCurrentImage"
            clip-path="url(#circleView)"
          />
        </svg>
      </div>
    </div>
    <AsSeenOnLogos
      class="main-header__as-seen-on-logos is--bottom"
      :class="{ 'is--no-offerings': !isFarmExist }"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, nextTick } from 'vue';
import { useFarmListOpenedSorted, useLogin } from 'Store';
import { FARM_TYPE } from 'Config/farm-type';
import FormGetStarted from 'Components/FormGetStarted/FormGetStarted.vue';
import AsSeenOnLogos from 'Components/AsSeenOnLogos/AsSeenOnLogos.vue';
import FarmSlider from 'Components/FarmSlider/FarmSlider.vue';
import ContentHomeSlider from './ContentHomeSlider.vue';

export default defineComponent({
  name: 'ContentHomeHeader',
  components: {
    FormGetStarted,
    FarmSlider,
    AsSeenOnLogos,
    ContentHomeSlider,
  },
  setup() {
    const sliderCurrentImage = ref('');
    const currentSlideNumber = ref(1);
    const { farmListOpenedSorted } = useFarmListOpenedSorted();
    const { isLoggedIn } = useLogin();
    const isMounted = ref(false);
    const activeTitle = ref('Farmland');
    const titles = ['Farmland', 'Orchard', 'Vineyard', 'Timberland'];

    const isOnlyDummyFarm = computed(() => (
      farmListOpenedSorted.value.length === 1 && farmListOpenedSorted.value[0].type === FARM_TYPE.DUMMY
    ));

    const isFarmExist = computed(() => (
      Boolean(farmListOpenedSorted.value && farmListOpenedSorted.value?.length > 0 && !isOnlyDummyFarm.value)
    ));
    const isOneFarm = computed(() => farmListOpenedSorted.value && farmListOpenedSorted.value?.length === 1);

    // eslint-disable-next-line
    function onCurrentSlideChange(data: any) {
      currentSlideNumber.value = data.value;
      const title = titles.filter((item, key) => (key + 1) === currentSlideNumber.value);
      activeTitle.value = title[0] || 'Farmland';
    }
    onMounted(async () => {
      await nextTick();
      isMounted.value = true;
    });

    return {
      sliderCurrentImage,
      isFarmExist,
      isLoggedIn,
      isMounted,
      activeTitle,
      currentSlideNumber,
      onCurrentSlideChange,
      isOneFarm,
    };
  },
});
</script>

<style lang="sass" scoped>
@import '~Styles/_variables'
.main-header
  width: 100%
  max-width: 1170px
  margin: 0 auto
  margin-bottom: 110px
  padding: 0 15px
  padding-top: 75px
  display: flex
  flex-direction: row
  align-items: center
  flex-wrap: wrap
  @media screen and (max-width: 576px)
    opacity: 0
    transition: opacity 0.5s ease-in-out
  @media screen and (min-width: 1440px) and (min-height: 800px)
    height: 75vh
    align-items: center
  @media screen and (max-width: 767px)
    flex-direction: column
    padding-top: 15px
    margin-bottom: 100px
  &.is--mounted
    @media screen and (max-width: 576px)
      opacity: 1
      transition: opacity 0.5s ease-in-out
      .main-header-right
        opacity: 1
  &__wrapper
    display: flex
    flex-direction: row
    width: 100%
    align-items: center
    @media screen and (max-width: 767px)
      flex-wrap: wrap
    &.is--no-offerings
      @media screen and (max-width: 991px)
        justify-content: space-between
      @media screen and (max-width: 767px)
        flex-direction: column
    &.is--one-farm
      @media screen and (min-width: 768px) and (max-width: 1170px)
        align-items: flex-start
  &.is--offerings
    @media screen and (min-width: 768px) and (max-width: 1170px)
      justify-content: space-between
  &__as-seen-on-logos
    margin-bottom: 0
    flex-direction: column
    width: 100%
    display: none
    @media screen and (min-width: 768px)
      display: flex
      margin-top: 30px
    :deep(.media-bar__item)
      &:last-child
        @media screen and (max-width: 991px)
          display: none
    &.is--offerings
      display: flex
    &.is--bottom
      @media screen and (min-width: 768px)
        display: none
    &.is--no-offerings
      display: flex
      order: 3
      margin-top: 65px
      @media screen and (min-width: 768px)
        display: none
  &__get-started
    &.is--not-logged-in
      @media screen and (min-width: 768px)
        background: $white
        box-shadow: 0px 5px 40px -10px rgba($bunker, 0.15)
        padding: 10px
        max-width: 588px
  &__left
    width: 100%
    max-width: 589px
    flex-direction: column
    display: flex
    justify-content: space-between
    height: 490px
    position: relative
    z-index: 2
    @media screen and (max-width: 1050px) and (min-width: 768px)
      max-width: 540px
    @media screen and (max-width: 767px)
      order: 2
      max-width: 100%
      position: static
      z-index: 3
      height: 100%
    &.is--no-offerings
      order: -1
    &.is--offerings
      height: 510px
      @media screen and (min-width: 768px) and (max-width: 1170px)
        height: 420px
      @media screen and (max-width: 767px)
        height: 100%
    &.is--one-farm
      height: 540px
      @media screen and (min-width: 768px) and (max-width: 1170px)
        height: 460px
      @media screen and (max-width: 767px)
        height: 100%
  &__right
    margin-left: 65px
    width: 100%
    max-width: 392px
    opacity: 1
    transition: opacity 0.2s ease-in-out
    @media screen and (max-width: 991px)
      margin-left: 30px
    @media screen and (max-width: 767px)
      max-width: 392px
      margin: 0 auto
    @media screen and (max-width: 576px)
      max-width: 100%
      height: 100%
  &__farm-background
    position: absolute
    right: 0
    top: -50px
    z-index: 1
    background-repeat: no-repeat
    background-size: cover
    background-position: top right
    width: 49%
    height: 704px
    opacity: 0.4
    -moz-backface-visibility: hidden
    -moz-perspective: 1000
    -moz-filter: blur(200px)
    -webkit-filter: blur(200px)
    filter: blur(200px)
    -webkit-backface-visibility: hidden
    -webkit-transform: translate3d(0, 0, 0)
    -moz-transform: translate3d(0, 0, 0)
    @-moz-document url-prefix()
      opacity: 0.1
    @media not all and (min-resolution: .001dpcm)
      display: none
    @media screen and (max-width: 767px)
      display: none
  &__farm-background-image
    width: 100%
    height: 100%
.main-header-left
  &.is--no-offerings
    margin-top: 30px
    margin-bottom: 50px
    @media screen and (max-width: 991px)
      height: auto
    .main-header-left__title
      color: $primary
    .main-header-left__subheading
      color: $boulder
    .main-header-left__subheading-star
      color: $text_gray
    .main-header-left__title-wrapper
      gap: 1rem
      @media screen and (max-width: 767px) and (min-width: 577px)
        flex-direction: row
    .main-header-left__title-absolute
      @media screen and (max-width: 767px) and (min-width: 577px)
        left: 12px
    .main-header-left__text
      @media screen and (max-width: 767px)
        margin-bottom: 35px
  &__wrapper
    &.is--offerings
      @media screen and (max-width: 767px)
        position: absolute
        left: 48%
        top: 210px
        transform: translate(-50%, 0)
        z-index: 3
        max-width: 312px
      @media screen and (max-width: 576px)
        left: 35px
        padding-right: 35px
        transform: translate(0, 0)
      @media screen and (max-width: 340px)
        max-width: 265px
  &__text
    margin-bottom: 45px
    width: 100%
    max-width: 588px
    @media screen and (max-width: 767px)
      margin-bottom: 20px
  &__title
    position: relative
    margin-bottom: 20px
    @media screen and (max-width: 991px)
      font-size: 30px
      line-height: 40px
    @media screen and (max-width: 767px)
      color: $white
      margin-bottom: 5px
    @media screen and (max-width: 369px)
      max-width: 310px
  &__title-wrapper-absolute
    position: relative
    @media screen and (max-width: 767px)
      height: 45px
  &__title-absolute
    @media screen and (max-width: 767px)
      left: 0
    &:before
      content: ''
      position: absolute
      left: 0
      bottom: 8px
      width: calc(100%)
      height: 2px
      background-color: $bunker
      @media screen and (max-width: 767px)
        bottom: 5px
  &__title-wrapper
    position: relative
    display: flex
  &__subheading
    @media screen and (max-width: 767px)
      color: $white
  &__subheading-star
    color: $text_gray
    @media screen and (max-width: 767px)
      color: $white
.main-header-right
  &.is--no-offerings
    @media screen and (max-width: 1320px)
      margin-left: 30px
    @media screen and (max-width: 991px)
      max-width: 350px
    @media screen and (max-width: 767px)
      max-width: 350px
      margin: 0 auto
    @media screen and (max-width: 374px)
      width: 290px
    .main-header-right__pattern-slider
      @media screen and (max-width: 991px)
        height: 330px
      @media screen and (max-width: 374px)
        height: 310px
  &__slider
    position: relative
    z-index: 2
    @media screen and (min-width: 768px) and (max-width: 1170px)
      height: 530px !important
    @media screen and (max-width: 767px)
      margin-bottom: 65px
    :deep(.farm-slider-navigation)
      @media screen and (max-width: 767px)
        margin-top: 20px
      @media screen and (max-width: 576px)
        max-width: 320px
        margin: 0 auto
        margin-top: 20px
    :deep(.farm-item)
      @media screen and (max-width: 1170px)
        max-width: 100%
        background-color: transparent
      .farm-item-header
        @media screen and (max-width: 1170px)
          max-width: 392px
        @media screen and (max-width: 767px)
          &:before
            content: ''
            position: absolute
            left: 0
            top: 0
            width: 100%
            height: 100%
            z-index: 1
            background: linear-gradient(180deg, $bunker 0%, rgba($bunker, 0.804124) 38.56%, rgba($bunker, 0) 100%)
            opacity: 0.7
            border-radius: 10px
        @media screen and (max-width: 576px)
          max-width: 100%
.slide-up-enter-active,
.slide-up-leave-active
  transition: all 0.2s ease-in-out
.slide-up-enter-from
  opacity: 0
  transform: translateY(-10px)
.slide-up-leave-to
  opacity: 0
  transform: translateY(10px)
</style>
